/**
 * De balk met de aftelling.
 *
 * Zie `docs/architecture/gelijktijdigheid.md` §7. Wie tijdens de aftelling aan
 * iets nieuws begint hoort de resterende tijd te zien op het moment dat 'ie
 * beslist — informatie in plaats van een deur die dicht zit. Daarom staat 'ie
 * boven het werk en niet in een hoek.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PendingOperationView } from "../api/client";
import { useOperations } from "../state/operations";
import { useSession } from "../state/session";
import { OperationPreviewDialog } from "./OperationPreviewDialog";

/** Resterende tijd als m:ss. Loopt in de client, want een teller die elke
 *  seconde de server bevraagt is een teller die de server opeet; het moment
 *  zelf komt van de server. */
function useRemaining(startsAt: string): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);
  return Math.max(0, Date.parse(startsAt) - now);
}

function formatRemaining(ms: number): string {
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function OperationRow({ operation }: { operation: PendingOperationView }) {
  const { t } = useTranslation();
  const me = useSession((s) => s.me?.email ?? null);
  const respond = useOperations((s) => s.respond);
  const [busy, setBusy] = useState(false);
  /** De lijst staat open of dicht; de inhoud reist mee met de operatie, want
   *  wie 'm ontvangt heeft het importbestand niet en kan de voorspelling dus
   *  niet zelf overdoen. */
  const [previewOpen, setPreviewOpen] = useState(false);
  const remaining = useRemaining(operation.startsAt);

  const mine = me === operation.requestedBy;
  // Alles behalve de aftelling zelf is alleen-lezen: de stilte, het moment dat
  // de teller op nul staat, en de uitvoering. De status komt van de server —
  // die heeft het slot er werkelijk op gezet.
  const quiet = operation.status !== "announced" || operation.phase !== "countdown";
  const running = operation.status === "running";
  const due = !running && remaining <= 0;
  const acked = operation.acks.some((a) => a.email === me);

  const act = (action: "ack" | "extend" | "start-now" | "cancel") => {
    setBusy(true);
    void respond(operation.id, action).finally(() => setBusy(false));
  };

  const kind = t(`operations.kind.${operation.kind}`);
  // Niet één opgeteld getal: "raakt 48 objecten" verbergt of er iets
  // verdwijnt. Overschrijven en verdwijnen zijn wat telt; nieuw laten we hier
  // weg — dat neemt niemand iets af.
  const overwritten = operation.impact?.overschreven ?? 0;
  const removed = operation.impact?.verdwijnt ?? 0;

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 text-xs ${
        quiet
          ? "bg-(--ld-notice-bg) text-(--ld-notice-ink)"
          : "bg-[color-mix(in_srgb,var(--ld-accent)_12%,var(--ld-surface))] text-(--ld-text)"
      }`}
    >
      <span className="font-semibold">
        {running
          ? t("operations.running", { kind })
          : due
            ? t("operations.dueNow", { kind, who: operation.requestedBy })
            : quiet
              ? t("operations.quietNow", { kind, time: formatRemaining(remaining) })
              : t("operations.countdown", {
                  kind,
                  who: operation.requestedBy,
                  time: formatRemaining(remaining),
                })}
      </span>
      <span className="opacity-80">{operation.label}</span>
      {removed > 0 && (
        <span className="font-semibold text-(--ld-negative)">
          {t("operations.impactRemoved", { count: removed })}
        </span>
      )}
      {overwritten > 0 && (
        <span className="opacity-80">
          {t("operations.impactOverwritten", { count: overwritten })}
        </span>
      )}
      {!quiet && <span className="opacity-70">{t("operations.keepWorking")}</span>}

      {/* Voor de aanvrager: wie er nog niet geklikt heeft, en wie om tijd vroeg. */}
      {mine && operation.awaiting.length > 0 && (
        <span className="opacity-70">
          {t("operations.awaiting", { who: operation.awaiting.join(", ") })}
        </span>
      )}
      {mine && operation.extensions.length > 0 && (
        <span className="opacity-70">
          {t("operations.askedForTime", {
            who: operation.extensions.map((e) => e.email).join(", "),
          })}
        </span>
      )}

      {/* De namen erbij halen. Aantallen zeggen niet of jóuw werk erbij zit. */}
      {operation.preview && (
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          className="underline decoration-dotted underline-offset-2"
        >
          {t("operations.preview.show")}
        </button>
      )}

      {previewOpen && operation.preview && (
        <OperationPreviewDialog
          preview={operation.preview}
          label={operation.label}
          onCancel={() => setPreviewOpen(false)}
        />
      )}

      <span className="ml-auto flex items-center gap-1">
        {running ? null : mine ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => act("start-now")}
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 hover:bg-(--ld-active) disabled:opacity-50"
            >
              {t("operations.startNow")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => act("cancel")}
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 hover:bg-(--ld-raised) disabled:opacity-50"
            >
              {t("operations.cancel")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={busy || acked}
              onClick={() => act("ack")}
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 hover:bg-(--ld-active) disabled:opacity-50"
            >
              {acked ? t("operations.readyDone") : t("operations.ready")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => act("extend")}
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 hover:bg-(--ld-raised) disabled:opacity-50"
            >
              {t("operations.needMoreTime")}
            </button>
          </>
        )}
      </span>
    </div>
  );
}

export function OperationBanner() {
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const operations = useOperations((s) => s.operations);
  const start = useOperations((s) => s.start);
  const stop = useOperations((s) => s.stop);

  useEffect(() => {
    if (!workspaceId || !branchId) return;
    start(workspaceId, branchId);
    return () => stop();
  }, [workspaceId, branchId, start, stop]);

  if (operations.length === 0) return null;
  return (
    <div className="ledger shrink-0 divide-y divide-(--ld-border) border-(--ld-border) border-b">
      {operations.map((op) => (
        <OperationRow key={op.id} operation={op} />
      ))}
    </div>
  );
}
